import apiFetch from "@wordpress/api-fetch";
import { useCallback, useMemo, useState } from "react";
import { FieldsConfigResponse } from "@hooks/use-config-context/types";
import { isFieldSelect, isFieldTaxonomy, isFieldUserInput } from "@hooks/use-config/utils";
import { useGetTaxonomyValues } from "@hooks/use-get/taxonomy-values";
import { useMultiSiteContext } from "@hooks/use-site-context";


export const useConfig = (CONFIG_URL: string) => {
  const [state, setState] = useState<FieldsConfigResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getApiUrl } = useMultiSiteContext();
  const { getTaxonomies } = useGetTaxonomyValues();
  const configUrl = useMemo(() => getApiUrl(CONFIG_URL), [getApiUrl, CONFIG_URL]);

  const getConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<FieldsConfigResponse>({
        url: configUrl,
        method: "GET",
      });
      const taxonomyFields = Object.values(data)
        .filter((field) => isFieldTaxonomy(field))
        .map((field) => field.taxonomy?.[0]) as string[];
      const taxonomyValues = await getTaxonomies(taxonomyFields);

      // Map taxonomy values back to the fields in the config
      const updatedData = Object.entries(data).reduce((acc, [key, field]) => {
        if (
          isFieldTaxonomy(field) ||
          isFieldUserInput(field) ||
          isFieldSelect(field)
        ) {
          const taxonomyIndex = taxonomyValues?.findIndex(
            (taxonomy) => taxonomy[0].taxonomy === field.taxonomy?.[0]
          );
          acc[key] = {
            ...field,
            taxonomy: taxonomyValues?.[taxonomyIndex ?? 0] || [],
            options: isFieldUserInput(field)
              ? []
              : Object.entries(field?.options ?? {})?.map(([key, value]) => ({
                  label: value as string,
                  value: key,
                })),
          };
        } else {
          acc[key] = field;
        }
        return acc;
      }, {} as FieldsConfigResponse);

      setState(updatedData);
      setLoading(false);
      return updatedData;
    } catch (err) {
      setError("Error fetching config: " + (err as Error)?.message);
    }
    setLoading(false);
  }, [configUrl, getTaxonomies]);

  return {
    loading,
    error,
    state,
    getConfig,
  };
};
